import { useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

import { media } from "../../../styles/media";

const overlayFadeIn = keyframes`
  from { background: transparent; }
  to { background: var(--central-modal-overlay); }
`;

const containerAppear = keyframes`
  from {
    transform: translateY(16px) scale(0.96);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const CentralModalStyles = styled.div`
  --central-modal-overlay: ${({ theme }) =>
    `${theme.colors.primary[900]}40`};
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  background: transparent;
  z-index: 1100;
  top: 0;
  left: 0;
  pointer-events: none;
  visibility: hidden;

  &.modalOpen {
    background: var(--central-modal-overlay);
    pointer-events: auto;
    visibility: visible;
    overflow-y: auto;
    overscroll-behavior: contain;
    animation: ${overlayFadeIn} 0.25s cubic-bezier(0.4, 0, 0.2, 1) both;
  }

  .modalContainer {
    min-width: 330px;
    max-width: 90vw;
    max-height: 90%;
    overflow-y: auto;
    border-radius: ${({ theme }) => theme.radius[16]};
    transform-origin: center;
  }

  &.modalOpen .modalContainer {
    animation: ${containerAppear} 0.32s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  /* Full-width sheet on phones — !important overrides the per-call inline
     pixel width that media queries otherwise can't beat. */
  ${({ theme }) => media.maxMobile`
    padding: 16px;

    .modalContainer {
      width: 100% !important;
      min-width: 0;
      max-width: 100%;
    }
  `}
`;

const CentralModal = ({
  className,
  children,
  closeModal,
  isOpen = true,
  width,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const containerStyle = width
    ? { width: typeof width === "number" ? `${width}px` : width }
    : undefined;

  return createPortal(
    <CentralModalStyles
      className={isOpen ? "modalOpen" : ""}
      onClick={closeModal}
    >
      <div
        className={`modalContainer ${className || ""}`}
        style={containerStyle}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </CentralModalStyles>,
    document.body
  );
};

export default CentralModal;
